/**
 * CommsHeader — navy header row for the /phone v2 workspace.
 *
 * PART 2 (SHELL): "Communications" + outbound-line DROPDOWN. Right side:
 * duty toggle with a REAL registration indicator (green = available AND
 * Voice SDK registered, amber = registered but token expired with
 * Reconnect action, gray = unavailable). Also the "use the old page"
 * escape hatch pill — required per PART 0 preamble.
 *
 * The registration indicator reads from useCall()'s exposed device state.
 * If the exposed state doesn't include the expiry, we conservatively
 * degrade to green when active/registered and amber otherwise. Any
 * required signal that is genuinely missing from useCall() is surfaced
 * in the report (see PART 9.b).
 */
import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronDown, Phone, RefreshCw, ArrowLeftRight, Globe, UserSearch } from "lucide-react";
import { useCall } from "../../components/layout/ActiveCallOverlay";
import { useAuth } from "../../context/AuthContext";
import { formatPhoneNumber } from "../../lib/utils";
import { NAVY, GOLD, GREEN, AMBER } from "./tokens";

const API = process.env.REACT_APP_BACKEND_URL;

function useFlaggedCount() {
  // Iter 507 · surface the "Unknown Contacts" review queue on the /phone
  // header so voicemails from unknown numbers don't pile up unseen.
  // Fetch once at mount; refresh every 60s while the tab is visible.
  const [count, setCount] = useState(null);
  const load = useCallback(async () => {
    try {
      const tok = localStorage.getItem("token");
      const r = await fetch(`${API}/api/contact-registry/stats`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!r.ok) return;
      const j = await r.json();
      setCount(j.flagged_for_review ?? 0);
    } catch { /* silent — pill just won't render count */ }
  }, []);
  useEffect(() => {
    load();
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 60_000);
    return () => clearInterval(iv);
  }, [load]);
  return count;
}

export default function CommsHeader({ companies, fromNumber, setFromNumber }) {
  const call = useCall() || {};
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const scopeAll = params.get("scope") === "all";
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const active = companies.find(c => c.phone_number === fromNumber);
  const isSuperAdmin = user?.role === "super_admin";
  const flaggedCount = useFlaggedCount();

  // Close dropdown on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (!btnRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Duty + registration indicator.
  // useCall exposes: onDuty (bool) + deviceStatus ('ready' | 'registering'
  // | 'destroyed' | 'error' | undefined). We derive:
  //   green  → onDuty && deviceStatus === 'ready'
  //   amber  → onDuty && deviceStatus !== 'ready'  (offer Reconnect)
  //   gray   → !onDuty
  const onDuty  = !!call.onDuty;
  const ready   = call.deviceStatus === "ready";
  const dutyDot =
    !onDuty ? "#94a3b8"                       /* slate-400 – gray */
    : ready  ? GREEN
    :          AMBER;
  const dutyLabel = !onDuty ? "Off duty" : ready ? "On duty" : "Reconnect";

  return (
    <div
      className="flex items-center gap-2 sm:gap-4 px-3 sm:px-4 h-14 shrink-0"
      style={{ background: NAVY, color: "white" }}
      data-testid="comms2-header"
    >
      <div className="font-[Montserrat] font-semibold text-[16px] sm:text-[18px] tracking-tight shrink-0">
        Communications
      </div>
      <span className="opacity-40 hidden sm:inline">·</span>

      {/* Outbound-line dropdown — takes remaining space and truncates on
          small screens instead of pushing the duty toggle off-canvas. */}
      <div className="relative min-w-0 flex-1 sm:flex-none" ref={btnRef}>
        <button
          onClick={() => setOpen(v => !v)}
          className="flex items-center gap-2 h-8 px-2 sm:px-3 rounded-md text-[13px] font-medium hover:bg-white/10 transition w-full sm:w-auto max-w-full"
          data-testid="comms2-line-picker"
        >
          {scopeAll ? (
            <span className="flex items-center gap-1.5 min-w-0">
              <Globe className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">All companies</span>
            </span>
          ) : (
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="truncate">{active?.name || "Choose line"}</span>
              <span className="opacity-60 font-mono hidden md:inline">{active ? formatPhoneNumber(active.phone_number) : ""}</span>
            </span>
          )}
          <ChevronDown className="w-3.5 h-3.5 opacity-70 shrink-0" />
        </button>
        {open && (
          <div className="absolute top-full left-0 mt-1 w-72 rounded-lg bg-white text-slate-800 shadow-2xl border border-slate-200 py-1 z-50">
            {/* Iter 484 — super_admin can un-scope the inbox to see
                every tenant's messages at once. Regular admins /
                employees can only see their own tenant. */}
            {isSuperAdmin && (
              <button
                onClick={() => {
                  setParams(p => {
                    const n = new URLSearchParams(p);
                    n.set("scope", "all");
                    return n;
                  }, { replace: true });
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 ${scopeAll ? "bg-slate-50" : ""}`}
                data-testid="comms2-line-all"
              >
                <Globe className="w-4 h-4" style={{ color: NAVY }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium">All companies</div>
                  <div className="text-[11px] text-slate-500">Super-admin: see every tenant's inbox</div>
                </div>
                {scopeAll && <span className="text-[10px] font-bold" style={{ color: NAVY }}>ACTIVE</span>}
              </button>
            )}
            {companies.map(c => (
              <button
                key={c.id || c.phone_number}
                onClick={() => {
                  setFromNumber(c.phone_number);
                  setParams(p => {
                    const n = new URLSearchParams(p);
                    n.delete("scope");
                    return n;
                  }, { replace: true });
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 ${(!scopeAll && active?.phone_number === c.phone_number) ? "bg-slate-50" : ""}`}
                data-testid={`comms2-line-${c.phone_number}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">{c.name}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{formatPhoneNumber(c.phone_number)}</div>
                </div>
                {c.phone_number === fromNumber && <span className="text-[11px] font-semibold" style={{ color: NAVY }}>Active</span>}
              </button>
            ))}
            {companies.length === 0 && (
              <div className="px-3 py-3 text-[12px] text-slate-500">No lines available. Ask an admin to assign one.</div>
            )}
          </div>
        )}
      </div>

      <div className="ml-auto flex items-center gap-2 sm:gap-3 shrink-0">
        {/* Iter 507 · Unknown Contacts queue — voicemails / inbound
            comms from unknown numbers pile up in this queue when the
            spine can't match them. Surfacing the count here so ops
            actually empties it. Hides when zero (no noise). */}
        {flaggedCount != null && flaggedCount > 0 && (
          <Link
            to="/admin/contact-registry"
            className="flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[12px] font-semibold hover:opacity-90 transition"
            style={{ background: GOLD, color: NAVY }}
            data-testid="comms2-flagged-link"
            title="Unknown contacts awaiting review"
          >
            <UserSearch className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Unknown</span>
            <span className="tabular-nums font-bold">{flaggedCount}</span>
          </Link>
        )}

        {/* Escape hatch — "use the old page". Required by PART 0 preamble
            because "Users are already frustrated with glitches; they
            need an exit." Removes the ?v=2 query so CommsEntry falls
            back to v1. Hidden on mobile so the header doesn't overflow;
            still reachable from Settings / Help menu. */}
        <div className="hidden md:block">
          <BackToOldPagePill />
        </div>

        {/* Duty toggle w/ real registration indicator */}
        <button
          onClick={() => call.setOnDuty && call.setOnDuty(!onDuty)}
          className="flex items-center gap-1.5 sm:gap-2 h-8 px-2 sm:px-3 rounded-md text-[12px] sm:text-[13px] font-medium hover:bg-white/10 transition"
          data-testid="comms2-duty-toggle"
          title={ready ? "Registered with Twilio Voice" : onDuty ? "Token expired — click Reconnect" : "Not on duty"}
        >
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ background: dutyDot }}
            aria-label={dutyLabel}
          />
          <span>{dutyLabel}</span>
          {onDuty && !ready && (
            <span
              className="ml-1 inline-flex items-center gap-1 text-[11px] font-semibold rounded px-1.5 py-0.5"
              style={{ background: GOLD, color: NAVY }}
              onClick={(e) => { e.stopPropagation(); call.reconnect && call.reconnect(); }}
              data-testid="comms2-reconnect"
            >
              <RefreshCw className="w-3 h-3" /> Reconnect
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

function BackToOldPagePill() {
  const [, setParams] = useSearchParams();
  return (
    <button
      onClick={() => setParams(p => {
        const n = new URLSearchParams(p);
        // Iter 483 — v2 is now the default. Set `?v=1` explicitly
        // to jump back to the iframe-tab layout as an escape hatch.
        n.set("v", "1");
        return n;
      }, { replace: true })}
      className="flex items-center gap-1.5 h-8 px-3 rounded-md text-[12px] font-medium hover:bg-white/10 transition"
      data-testid="comms2-use-old-page"
      title="Escape hatch — go back to the previous /phone page"
    >
      <ArrowLeftRight className="w-3.5 h-3.5 opacity-80" />
      Use the old page
    </button>
  );
}
